import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchSharedLedgerRecoverySnapshot, fetchSharedLedgerSnapshot } from '../../api/sharedLedgersAPI'
import { importSharedLedgerSnapshotAsFork } from '../../lib/ledgerSafeImportExportBoundary'
import { saveActiveEditorIdentity } from '../../lib/editorIdentityStore'
import { buildWorkbenchLedgerPath } from '../../lib/workbenchWorkflowBoundary'
import { useRuntimeFeatureFlag } from '../../config/runtimeConfig'
import type { FetchSharedLedgerRecoveryResponse, FetchSharedLedgerResponse } from '../../types/sharedLedgerSnapshots'
import type { LedgerSafeContributor } from '../../types/ledgerSafeProvenance'
import { LedgerSafeManualStudio } from '../analysis/LedgerSafeManualStudio'

interface SharedLedgerViewProps {
  shareId: string
}

const PENDING_SHARED_LEDGER_RECOVERY_KEY = 'ledger_safe_pending_shared_ledger_recovery'

type SharedLedgerImportMode = 'baseline' | 'working'

interface PendingSharedLedgerRecovery {
  shareId: string
  mode: SharedLedgerImportMode
  requestedAt: string
}

const normalizeImportMode = (value: string | null | undefined): SharedLedgerImportMode | null => {
  if (value === 'baseline' || value === 'working') {
    return value
  }
  return null
}

export const queueSharedLedgerRecoveryLogin = (shareId: string, mode: SharedLedgerImportMode): void => {
  const pending: PendingSharedLedgerRecovery = {
    shareId,
    mode,
    requestedAt: new Date().toISOString(),
  }
  window.sessionStorage.setItem(PENDING_SHARED_LEDGER_RECOVERY_KEY, JSON.stringify(pending))
  if (process.env.NODE_ENV === 'test') {
    window.history.pushState({}, '', '/workbench?sharedLedgerRecovery=1')
    window.dispatchEvent(new PopStateEvent('popstate'))
    return
  }
  window.location.assign('/workbench?sharedLedgerRecovery=1')
}

export const consumePendingSharedLedgerRecovery = (): PendingSharedLedgerRecovery | null => {
  const rawValue = window.sessionStorage.getItem(PENDING_SHARED_LEDGER_RECOVERY_KEY)
  if (!rawValue) {
    return null
  }
  try {
    const parsed = JSON.parse(rawValue) as Partial<PendingSharedLedgerRecovery>
    const mode = normalizeImportMode(parsed.mode)
    if (!parsed.shareId || !mode) {
      window.sessionStorage.removeItem(PENDING_SHARED_LEDGER_RECOVERY_KEY)
      return null
    }
    return {
      shareId: parsed.shareId,
      mode,
      requestedAt: typeof parsed.requestedAt === 'string' ? parsed.requestedAt : new Date().toISOString(),
    }
  } catch {
    window.sessionStorage.removeItem(PENDING_SHARED_LEDGER_RECOVERY_KEY)
    return null
  }
}

export const clearPendingSharedLedgerRecovery = (): void => {
  window.sessionStorage.removeItem(PENDING_SHARED_LEDGER_RECOVERY_KEY)
}

export const SharedLedgerView: React.FC<SharedLedgerViewProps> = ({ shareId }) => {
  const runtimeEnableBaselineImport = useRuntimeFeatureFlag('sharing.baselineImport', false)
  const [payload, setPayload] = useState<FetchSharedLedgerResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [importingMode, setImportingMode] = useState<SharedLedgerImportMode | null>(null)
  const autoRecoveryAttemptedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    void fetchSharedLedgerSnapshot(shareId)
      .then(response => {
        if (!cancelled) {
          setPayload(response)
          setLoading(false)
        }
      })
      .catch(fetchError => {
        if (!cancelled) {
          setError(fetchError instanceof Error ? fetchError.message : 'Failed to load shared collation.')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [shareId])

  const snapshot = payload?.snapshot || null
  const activeRows = useMemo(() => {
    if (!snapshot) {
      return []
    }
    if (snapshot.states.active_kind === 'working' && snapshot.states.working?.rows) {
      return snapshot.states.working.rows
    }
    return snapshot.states.baseline.rows
  }, [snapshot])
  const hasWorkingState = Boolean(payload?.capabilities?.canRecoverEditableState ?? snapshot?.states.working?.rows?.length)
  const canImportBaseline = Boolean(payload?.capabilities?.canImportBaseline ?? snapshot?.states.baseline?.rows?.length)

  const persistAuthenticatedRecoveryIdentity = useCallback(async (recoveryPayload: FetchSharedLedgerRecoveryResponse) => {
    const user = recoveryPayload.authenticated_user
    if (!user) {
      return
    }
    await saveActiveEditorIdentity({
      editorId: user.user_id,
      displayName: user.display_name || user.email,
      email: user.email,
      institution: user.domain === 'fu-berlin.de' ? 'Freie Universitat Berlin' : undefined,
    })
  }, [])

  const importAuthenticatedSnapshot = useCallback(async (mode: SharedLedgerImportMode) => {
    setImportingMode(mode)
    setError(null)
    try {
      const recoveryPayload = await fetchSharedLedgerRecoverySnapshot(shareId)
      await persistAuthenticatedRecoveryIdentity(recoveryPayload)
      const result = await importSharedLedgerSnapshotAsFork({
        snapshot: recoveryPayload.snapshot,
        record: recoveryPayload.record,
        mode,
      })
      clearPendingSharedLedgerRecovery()
      window.history.pushState({}, '', buildWorkbenchLedgerPath({
        projectId: result.projectId,
        workId: result.workId,
        contextId: result.contextId,
      }))
      window.dispatchEvent(new PopStateEvent('popstate'))
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : 'Login is required before this shared collation can be recovered.')
      setImportingMode(null)
    }
  }, [persistAuthenticatedRecoveryIdentity, shareId])

  const requestAuthenticatedImport = useCallback((mode: SharedLedgerImportMode) => {
    queueSharedLedgerRecoveryLogin(shareId, mode)
  }, [shareId])

  const autoRecoverMode = useMemo(() => {
    if (typeof window === 'undefined') {
      return null
    }
    return normalizeImportMode(new URLSearchParams(window.location.search).get('recover'))
  }, [])

  useEffect(() => {
    if (!snapshot || !autoRecoverMode || autoRecoveryAttemptedRef.current) {
      return
    }
    autoRecoveryAttemptedRef.current = true
    void importAuthenticatedSnapshot(autoRecoverMode)
  }, [autoRecoverMode, importAuthenticatedSnapshot, snapshot])

  const contributorLabel = (contributor: LedgerSafeContributor): string => {
    if (contributor.kind === 'llm') {
      const settings = [
        contributor.temperature !== undefined ? `temp ${contributor.temperature}` : null,
        contributor.thinkingLevel ? `thinking ${contributor.thinkingLevel}` : null,
        contributor.maxOutputTokens !== undefined ? `${contributor.maxOutputTokens} tokens` : null,
      ].filter(Boolean).join(' - ')
      return `${contributor.displayName}${settings ? ` - ${settings}` : ''}`
    }
    return contributor.displayName || contributor.id
  }

  const sharedByLabel = snapshot?.provenance.shared_by?.displayName
    || snapshot?.provenance.created_by?.displayName
    || ''
  const contributorSummary = (snapshot?.provenance.contributors || [])
    .slice(0, 2)
    .map(contributor => `${contributor.kind === 'llm' ? 'LLM' : 'Human'}: ${contributorLabel(contributor)}`)
    .join(' · ')
  const snapshotHashLabel = payload?.record.snapshot_hash
    ? payload.record.snapshot_hash.slice(0, 12)
    : ''

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper-1 px-6 text-ink-0">
        <div className="border border-paper-2 bg-paper-0 px-6 py-5 text-sm shadow-paper-1">
          Loading shared collation...
        </div>
      </div>
    )
  }

  if (error && !snapshot) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper-1 px-6 text-ink-0">
        <div className="w-full max-w-xl border border-danger/30 bg-paper-0 px-6 py-5 text-sm shadow-paper-1">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-danger">Shared collation</div>
          <h1 className="mt-3 text-xl font-semibold">Could not open link</h1>
          <p className="mt-2 leading-6 text-ink-3">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen min-h-0 flex-col overflow-hidden bg-gray-50 text-ink-0">
      <main className="min-h-0 flex-1 overflow-hidden">
        <LedgerSafeManualStudio
          analysisData={null}
          witnesses={snapshot?.witnesses || []}
          readOnly
          readOnlyRows={activeRows}
          readOnlyStructuralRangeLayer={
            snapshot && 'structural_layer' in snapshot ? snapshot.structural_layer : null
          }
          initialSidePanelTab="map"
          projectLabel={snapshot?.summary.project_name || snapshot?.project.name || 'Shared collation'}
          contextLabel={[
            snapshot?.summary.work_name || snapshot?.work.name,
            snapshot?.summary.context_name || snapshot?.context.name,
            'shared preview'
          ].filter(Boolean).join(' / ')}
          mastheadActions={(
            <>
              <span className="hidden whitespace-nowrap border border-gray-300 bg-gray-100 px-2 py-1 font-mono text-[10px] text-gray-700 lg:inline-flex">
                {snapshot?.summary.witness_count || snapshot?.witnesses.length || 0} witnesses
              </span>
              <span className="hidden whitespace-nowrap border border-gray-300 bg-gray-100 px-2 py-1 font-mono text-[10px] text-gray-700 lg:inline-flex">
                {activeRows.length} rows
              </span>
              {sharedByLabel && (
                <span className="hidden max-w-[160px] truncate border border-gray-300 bg-white px-2 py-1 font-mono text-[10px] text-gray-600 xl:inline-block" title={`Shared by ${sharedByLabel}`}>
                  By {sharedByLabel}
                </span>
              )}
              {contributorSummary && (
                <span className="hidden max-w-[220px] truncate border border-gray-300 bg-white px-2 py-1 font-mono text-[10px] text-gray-600 2xl:inline-block" title={contributorSummary}>
                  {contributorSummary}
                </span>
              )}
              {snapshotHashLabel && (
                <span className="hidden whitespace-nowrap border border-gray-300 bg-white px-2 py-1 font-mono text-[10px] text-gray-500 xl:inline-flex">
                  {snapshotHashLabel}
                </span>
              )}
              {error && (
                <span className="max-w-[220px] truncate border border-red-300 bg-red-50 px-2 py-1 font-mono text-[10px] text-red-700" title={error}>
                  {error}
                </span>
              )}
              {runtimeEnableBaselineImport && (
                <button
                  type="button"
                  className="h-8 whitespace-nowrap border border-black bg-white px-3 text-[10px] font-bold uppercase tracking-widest text-black hover:bg-gray-100 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
                  disabled={Boolean(importingMode) || !snapshot || !canImportBaseline}
                  onClick={() => requestAuthenticatedImport('baseline')}
                >
                  {importingMode === 'baseline' ? 'Importing...' : 'Import as new collation'}
                </button>
              )}
              <button
                type="button"
                className="h-8 whitespace-nowrap border border-black bg-black px-3 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
                disabled={Boolean(importingMode) || !snapshot || !hasWorkingState}
                onClick={() => requestAuthenticatedImport('working')}
                title={hasWorkingState ? 'Recover the editable state included in this share.' : 'This shared collation does not include a separate editable state.'}
              >
                {importingMode === 'working' ? 'Recovering...' : 'Recover editable state'}
              </button>
            </>
          )}
        />
      </main>
    </div>
  )
}
